import { SeawardException } from '@ch-types/SeawardExceptions';
import { IArgumentType } from '@ch-types/Exception';

/**
 * Base exception for all Bluetooth exceptions.
 */
export class BluetoothException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothException {
        return new BluetoothException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when bluetooth is not enabled.
 */
export class InvalidMacAddressException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidMacAddressException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidMacAddressException {
        return new InvalidMacAddressException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when bluetooth is not enabled.
 */
export class BluetoothNotEnabledException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothNotEnabledException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothNotEnabledException {
        return new BluetoothNotEnabledException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when pairing is cancelled/rejected.
 */
export class PairingCancelledException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'PairingCancelledException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): PairingCancelledException {
        return new PairingCancelledException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when pairing times out.
 */
export class PairingTimeoutException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'PairingTimeoutException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): PairingTimeoutException {
        return new PairingTimeoutException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when pairing fails.
 */
export class PairingFailureException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'PairingFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): PairingFailureException {
        return new PairingFailureException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when device type if filtered by a filter.
 */
export class DeviceFilteredException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'DeviceFilteredException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): DeviceFilteredException {
        return new DeviceFilteredException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when connection fails.
 */
export class ConnectionFailureException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'ConnectionFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): ConnectionFailureException {
        return new ConnectionFailureException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when printing fails.
 */
export class BluetoothPrintFailureException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothPrintFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothPrintFailureException {
        return new BluetoothPrintFailureException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when necessary arguments are missing.
 */
export class MissingArgumentsException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'MissingArgumentsException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): MissingArgumentsException {
        return new MissingArgumentsException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when provided device profile is invalid.
 */
export class InvalidProfileException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidProfileException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidProfileException {
        return new InvalidProfileException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when provided action is invalid.
 */
export class InvalidActionException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidActionException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidActionException {
        return new InvalidActionException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when fetching bluetooth profile uuids fails.
 */
export class FetchingUuidFailureException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'FetchingUuidFailureException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): FetchingUuidFailureException {
        return new FetchingUuidFailureException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when unknown error occurs in Bluetooth plugin.
 */
export class BluetoothUnknownErrorException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothUnknownErrorException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothUnknownErrorException {
        return new BluetoothUnknownErrorException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when authentication fails for seaward bluetooth modal
 */
export class BluetoothAuthorizationErrorException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothAuthorizationErrorException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothAuthorizationErrorException {
        return new BluetoothAuthorizationErrorException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Failed to close serial connection.
 */
export class DisconnectFailedException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'DisconnectFailedException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): DisconnectFailedException {
        return new DisconnectFailedException({
            message: obj.message,
        });
    }
}

/**
 * Exception thrown when Incomplete data received from the device
 */
export class IncompleteDataReceivedException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'IncompleteDataReceivedException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): IncompleteDataReceivedException {
        return new IncompleteDataReceivedException({
            message: obj.message,
        });
    }
}

export class InvalidArgsFormatException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InvalidArgsFormatException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InvalidArgsFormatException {
        return new InvalidArgsFormatException({
            message: obj.message,
        });
    }
}

export class InterruptedException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'InterruptedException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): InterruptedException {
        return new InterruptedException({
            message: obj.message,
        });
    }
}

export class UnsupportedCharsetException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnsupportedCharsetException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnsupportedCharsetException {
        return new UnsupportedCharsetException({
            message: obj.message,
        });
    }
}

export class UnsupportedGattEventException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'UnsupportedGattEventException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): UnsupportedGattEventException {
        return new UnsupportedGattEventException({
            message: obj.message,
        });
    }
}

export class BluetoothGattEventException extends BluetoothException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BluetoothGattEventException';
    }

    /**
     * Static `deserialize` method to the Constructor so that SeawardCrossFrameObjectTranslator can use it to
     * deserialize the exception.
     *
     * @param obj - Containing message and other data.
     * @param {string} model - A unique name for the new exception.
     */
    public deserialize(obj): BluetoothGattEventException {
        return new BluetoothGattEventException({
            message: obj.message,
        });
    }
}
